import { ImoClass, ImoClasses } from '@yard/components/types/ImoClasses'
import { DGSegregationRuleDataStore } from '../stores/dg-segregation-rule.data-store'
import { SnackbarStore } from '@yard/stores/snackbar.store'
import { makeAutoObservable, runInAction } from 'mobx'

export interface Cell {
  row: ImoClass
  col: ImoClass
}

export interface RuleCellValue {
  id: string
  distance: number
  isProhibited: boolean
}

export interface DGSegregationRuleFormValues {
  distance: number
  isProhibited: boolean
}

export class DGSegregationRulesTableUIStore {
  rules: Map<string, RuleCellValue> = new Map()
  isDragging = false
  isDialogOpen = false
  isResetDialogOpen = false
  startCell?: Cell
  endCell?: Cell

  constructor(
    public readonly _dgSegregationRuleDataStore: DGSegregationRuleDataStore,
    private readonly _snackbarStore: SnackbarStore,
  ) {
    makeAutoObservable(this)
  }

  toggleDialog() {
    this.isDialogOpen = !this.isDialogOpen
  }

  toggleResetDialog() {
    this.isResetDialogOpen = !this.isResetDialogOpen
  }

  getRule(row: ImoClass, col: ImoClass): RuleCellValue | null {
    return this.rules.get(this.getKey(row, col)) ?? null
  }

  getRuleDisplayValue(row: ImoClass, col: ImoClass): string | number {
    const rule = this.getRule(row, col)

    if (!rule) {
      return '-'
    }

    return rule.isProhibited ? 'X' : rule.distance
  }

  getRuleFormValues(row: ImoClass, col: ImoClass): DGSegregationRuleFormValues {
    const rule = this.getRule(row, col)

    return {
      distance: rule?.distance ?? 0,
      isProhibited: rule?.isProhibited ?? false,
    }
  }

  getSelectedRuleFormValues(): DGSegregationRuleFormValues {
    const selectedCell = this.getSingleSelectedCell()

    if (!selectedCell) {
      return { distance: 0, isProhibited: false }
    }

    return this.getRuleFormValues(selectedCell.row, selectedCell.col)
  }

  getSingleSelectedCell(): Cell | null {
    const selectedCells = this.getSelectedCells()

    return selectedCells.length === 1 ? selectedCells[0] : null
  }

  setRule(row: ImoClass, col: ImoClass, value: RuleCellValue | null) {
    if (value === null) {
      this.rules.delete(this.getKey(row, col))
    } else {
      this.rules.set(this.getKey(row, col), value)
    }
  }

  async loadAllDGSegregationRules() {
    const dgSegregationRules = await this._dgSegregationRuleDataStore.getAll()
    runInAction(() => {
      this.rules.clear()
      dgSegregationRules.forEach(rule => {
        this.rules.set(this.getKey(rule.imoClass1 as ImoClass, rule.imoClass2 as ImoClass), {
          id: rule.id,
          distance: rule.distance,
          isProhibited: rule.isProhibited,
        })
      })
    })
  }

  setIsDragging(dragging: boolean) {
    this.isDragging = dragging
  }

  setStartCell(row: ImoClass, col: ImoClass) {
    this.startCell = { row, col }
  }

  setEndCell(row: ImoClass, col: ImoClass) {
    this.endCell = { row, col }
  }

  handleMouseDown(row: ImoClass, col: ImoClass) {
    this.setIsDragging(true)
    this.setStartCell(row, col)
    this.setEndCell(row, col)
  }

  handleMouseEnter(row: ImoClass, col: ImoClass) {
    if (this.isDragging) {
      this.setEndCell(row, col)
    }
  }

  handleMouseUp() {
    if (this.isDragging) {
      this.setIsDragging(false)
      this.toggleDialog()
    }
  }

  async clear() {
    const selectedCells = this.getSelectedCells()
    const uniquePairs = new Set<string>()
    const rulesToUpsert: Array<{ imoClass1: string; imoClass2: string; distance: number; isProhibited: boolean }> = []

    selectedCells.forEach(({ row, col }) => {
      const key = this.getKey(row, col)
      if (!uniquePairs.has(key)) {
        uniquePairs.add(key)
        const [imoClass1, imoClass2] = key.split('-')
        rulesToUpsert.push({ imoClass1, imoClass2, distance: 0, isProhibited: false })
      }
    })

    try {
      if (rulesToUpsert.length > 0) {
        const updatedRules = await this._dgSegregationRuleDataStore.upsertBulk(rulesToUpsert)

        runInAction(() => {
          updatedRules.forEach(rule => {
            this.rules.set(this.getKey(rule.imoClass1 as ImoClass, rule.imoClass2 as ImoClass), {
              id: rule.id,
              distance: rule.distance,
              isProhibited: rule.isProhibited,
            })
          })
        })
      }

      this._snackbarStore.showMessage('DG segregation rules cleared successfully', 'success')
    } catch (e) {
      this._snackbarStore.showMessage('An unexpected error occurred while clearing the DG segregation rules', 'error')
    } finally {
      this.toggleDialog()
      this.clearSelection()
    }
  }

  clearSelection() {
    this.startCell = undefined
    this.endCell = undefined
  }

  closeDialog() {
    this.toggleDialog()
    this.clearSelection()
  }

  getSelectedCells() {
    if (!this.startCell || !this.endCell) return []
    const startRow = Math.min(
      ImoClasses.indexOf(this.startCell.row),
      ImoClasses.indexOf(this.endCell.row),
    )
    const endRow = Math.max(
      ImoClasses.indexOf(this.startCell.row),
      ImoClasses.indexOf(this.endCell.row),
    )
    const startCol = Math.min(
      ImoClasses.indexOf(this.startCell.col),
      ImoClasses.indexOf(this.endCell.col),
    )
    const endCol = Math.max(
      ImoClasses.indexOf(this.startCell.col),
      ImoClasses.indexOf(this.endCell.col),
    )

    const cells: Cell[] = []
    for (let i = startRow; i <= endRow; i++) {
      for (let j = startCol; j <= endCol; j++) {
        cells.push({ row: ImoClasses[i], col: ImoClasses[j] })
      }
    }
    return cells
  }

  async save(distance: number, isProhibited: boolean) {
    const selectedCells = this.getSelectedCells()
    const uniquePairs = new Set<string>()
    const rulesToUpsert: Array<{ imoClass1: string; imoClass2: string; distance: number; isProhibited: boolean }> = []
    const normalizedDistance = isProhibited ? 0 : distance

    selectedCells.forEach(({ row, col }) => {
      const key = this.getKey(row, col)
      if (!uniquePairs.has(key)) {
        uniquePairs.add(key)
        const [imoClass1, imoClass2] = key.split('-')
        rulesToUpsert.push({ imoClass1, imoClass2, distance: normalizedDistance, isProhibited })
      }
    })

    try {
      const updatedRules = await this._dgSegregationRuleDataStore.upsertBulk(rulesToUpsert)

      runInAction(() => {
        updatedRules.forEach(rule => {
          this.rules.set(this.getKey(rule.imoClass1 as ImoClass, rule.imoClass2 as ImoClass), {
            id: rule.id,
            distance: rule.distance,
            isProhibited: rule.isProhibited,
          })
        })
      })

      this._snackbarStore.showMessage('DG segregation rules updated successfully', 'success')
    } catch (e) {
      this._snackbarStore.showMessage('An unexpected error occurred while editing the DG segregation rules', 'error')
    } finally {
      this.toggleDialog()
      this.clearSelection()
    }
  }

  async resetToDefaults() {
    try {
      const defaultRules = await this._dgSegregationRuleDataStore.resetToDefaults()

      runInAction(() => {
        this.rules.clear()
        defaultRules.forEach(rule => {
          this.rules.set(this.getKey(rule.imoClass1 as ImoClass, rule.imoClass2 as ImoClass), {
            id: rule.id,
            distance: rule.distance,
            isProhibited: rule.isProhibited,
          })
        })
      })

      this._snackbarStore.showMessage('DG segregation rules reset to defaults successfully', 'success')
    } catch (e) {
      this._snackbarStore.showMessage('An unexpected error occurred while resetting the DG segregation rules', 'error')
    } finally {
      this.toggleResetDialog()
    }
  }

  isCellSelected(row: ImoClass, col: ImoClass) {
    if (!this.startCell || !this.endCell) return false
    const rowIndex = ImoClasses.indexOf(row)
    const colIndex = ImoClasses.indexOf(col)
    const startRowIndex = ImoClasses.indexOf(this.startCell.row)
    const endRowIndex = ImoClasses.indexOf(this.endCell.row)
    const startColIndex = ImoClasses.indexOf(this.startCell.col)
    const endColIndex = ImoClasses.indexOf(this.endCell.col)

    return (
      rowIndex >= Math.min(startRowIndex, endRowIndex) &&
      rowIndex <= Math.max(startRowIndex, endRowIndex) &&
      colIndex >= Math.min(startColIndex, endColIndex) &&
      colIndex <= Math.max(startColIndex, endColIndex)
    )
  }

  private getKey(row: ImoClass, col: ImoClass): string {
    return row <= col ? `${row}-${col}` : `${col}-${row}`
  }
}
